/**
 * cdt-wishlist — native wishlist engine for cudetoate.ro (replaces the
 * Wishlist Hero app). Port of Vivendo's vv-wishlist.js.
 *
 * - localStorage is the source of truth for display ('cdt_wl'); every
 *   add/remove is mirrored to the TenQ backend (fire-and-forget beacon)
 *   for analytics + email capture.
 * - Anonymous visitor id in 'cdt_wid' (UUID). Logged-in customers get their
 *   email linked to the visitor (transactional only). No customer-id merge —
 *   unverifiable without an App Proxy.
 * - Hearts: any element with [data-vv-wl-toggle] + data-pid/handle/title.
 *   Delegated click handler; state class 'is-saved'.
 * - Header heart + count bubble are Liquid-rendered (our replacement of
 *   snippets/wishlisthero-header-icon.liquid) — JS only keeps counts fresh.
 * - PDP: Wishlist Hero's app embed may still inject its own button into
 *   #wishlist-hero-product-page-button; a small guard removes app-injected
 *   containers so only our Liquid button remains.
 */
(function () {
  'use strict';
  var API = 'https://api.tenq.ro/api/wishlist';
  var SHOP = 'cudetoate';
  var LS_KEY = 'cdt_wl';
  var WID_KEY = 'cdt_wid';

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function wid() {
    var v = null;
    try { v = localStorage.getItem(WID_KEY); } catch (e) { /* noop */ }
    if (!v) {
      v = uuid();
      try { localStorage.setItem(WID_KEY, v); } catch (e) { /* noop */ }
    }
    return v;
  }

  function read() {
    try {
      var raw = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (e) { return []; }
  }
  function write(list) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(list.slice(0, 200))); } catch (e) { /* noop */ }
    document.dispatchEvent(new CustomEvent('vv:wishlist-change', { detail: { count: list.length } }));
  }

  function customerEmail() {
    return (document.body && document.body.dataset.cdtCustomerEmail) || null;
  }

  function beacon(path, payload) {
    try {
      fetch(API + path, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(function () { /* noop */ });
    } catch (e) { /* noop */ }
  }

  var api = {
    list: read,
    count: function () { return read().length; },
    has: function (pid) {
      pid = parseInt(pid, 10);
      return read().some(function (i) { return i.p === pid; });
    },
    add: function (item) {
      var pid = parseInt(item.p, 10);
      if (!pid) return;
      var list = read();
      if (!list.some(function (i) { return i.p === pid; })) {
        list.unshift({ p: pid, v: parseInt(item.v, 10) || null, h: item.h || null, t: item.t || null, pr: item.pr || null });
        write(list);
      }
      beacon('/event', {
        shop: SHOP, visitorId: wid(), action: 'add',
        productId: pid, variantId: item.v || null, handle: item.h || null,
        title: item.t || null, price: item.pr || null
      });
    },
    remove: function (pid) {
      pid = parseInt(pid, 10);
      write(read().filter(function (i) { return i.p !== pid; }));
      beacon('/event', { shop: SHOP, visitorId: wid(), action: 'remove', productId: pid });
    },
    toggle: function (item) {
      if (api.has(item.p)) { api.remove(item.p); return false; }
      api.add(item); return true;
    },
    identify: function (email, consent) {
      return fetch(API + '/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop: SHOP, visitorId: wid(), email: email, consent: !!consent })
      }).then(function (r) { return r.json(); });
    },
    visitorId: wid
  };
  window.cdtWishlist = api;

  /* ── Heart state sync ─────────────────────────────────────────────── */
  function syncHearts(root) {
    var list = read();
    var ids = {};
    list.forEach(function (i) { ids[i.p] = true; });
    (root || document).querySelectorAll('[data-vv-wl-toggle]').forEach(function (btn) {
      var saved = !!ids[parseInt(btn.dataset.pid, 10)];
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-vv-wl-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var saved = api.toggle({
      p: btn.dataset.pid,
      v: btn.dataset.vid,
      h: btn.dataset.handle,
      t: btn.dataset.title,
      pr: btn.dataset.price
    });
    btn.classList.toggle('is-saved', saved);
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    if (saved) {
      btn.classList.add('cdt-wl-pop');
      setTimeout(function () { btn.classList.remove('cdt-wl-pop'); }, 400);
    }
  });

  function updateHeaderCount() {
    var n = api.count();
    document.querySelectorAll('.cdt-wl-header-count').forEach(function (b) {
      b.textContent = n;
      b.classList.toggle('is-visible', n > 0);
    });
  }
  document.addEventListener('vv:wishlist-change', updateHeaderCount);

  /* ── Wishlist Hero cleanup guard ──────────────────────────────────
     The Hero app embed may still be enabled and inject its own button
     into #wishlist-hero-product-page-button (possibly wiping our Liquid
     button). Remove app containers and re-add ours if it got wiped. */
  var pdpButtonTemplate = null; // clone of our Liquid-rendered button
  function guardPdpButton() {
    var slot = document.getElementById('wishlist-hero-product-page-button');
    if (!slot) return;
    slot.querySelectorAll('[class*="wishlisthero"]').forEach(function (el) { el.remove(); });
    if (!slot.querySelector('[data-vv-wl-toggle]') && pdpButtonTemplate) {
      slot.appendChild(pdpButtonTemplate.cloneNode(true));
      syncHearts(slot);
    }
  }

  /* ── Logged-in email link (transactional only) ────────────────────── */
  function mergeServerList() {
    var email = customerEmail();
    if (email) {
      try {
        var k = 'cdt_wl_cid_linked';
        if (sessionStorage.getItem(k) !== email) {
          beacon('/identify', { shop: SHOP, visitorId: wid(), email: email, consent: false });
          sessionStorage.setItem(k, email);
        }
      } catch (e) { /* noop */ }
    }
  }

  function init() {
    var slot = document.getElementById('wishlist-hero-product-page-button');
    var ourBtn = slot && slot.querySelector('[data-vv-wl-toggle]');
    if (ourBtn) pdpButtonTemplate = ourBtn.cloneNode(true);
    syncHearts();
    updateHeaderCount();
    guardPdpButton();
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      guardPdpButton();
      updateHeaderCount();
      if (tries > 10) clearInterval(t);
    }, 1000);
    mergeServerList();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // re-sync hearts on dynamically injected content (quick shop, sliders)
  document.addEventListener('vv:cards-injected', function (e) { syncHearts(e.target); });
  window.cdtWishlistSyncHearts = syncHearts;
})();
